import { useEffect, useState } from 'preact/hooks';
import { CaptureGlyph as Knot } from '../components/CaptureGlyph';
import { getBetaInvite, confirmBetaInvite, ApiError } from '../lib/api';
import { useDeploymentSkin } from '../lib/skin';
import './BetaInvite.css';

export interface BetaInviteProps {
  /** The token out of the `/#/beta/<token>` hash — App.tsx's own route
   *  match, same convention as ReviewerPortal/RecordGrantPortal. */
  token: string;
}

type Phase =
  | 'loading'
  | 'refused'
  | 'form'
  | 'confirming'
  | 'submitting'
  | 'minted'
  | 'mailed';

const HEARD_FROM_MAX = 200;

// B1 §4 — the invite screen (Huan's own three questions, then a confirm
// step). Reached with NO session and NO tenant — the token is the only
// credential, same posture ReviewerPortal/RecordGrantPortal already take —
// so this imports only @vowric/core-free API helpers and Fastify-free
// components, never anything that assumes a signed-in shell.
export function BetaInvite({ token }: BetaInviteProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [refusalMessage, setRefusalMessage] = useState<string>('This invite link is not valid.');
  const [heardFrom, setHeardFrom] = useState('');
  const [performerName, setPerformerName] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  // B2 (2026-09-24): the walk found this screen rendering in the default
  // blue on every deployment — it never wore the pack's colours the way
  // Login.tsx does. Same hook, same reason: this IS the deployment's own
  // front door (the invite link), not a visitor's one-shot portal.
  useDeploymentSkin();

  useEffect(() => {
    let cancelled = false;
    getBetaInvite(token)
      .then(({ email }) => {
        if (cancelled) return;
        setAccountEmail(email);
        setPhase('form');
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.serverMessage) setRefusalMessage(err.serverMessage);
        setPhase('refused');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  function goToConfirm(): void {
    const heard = heardFrom.trim();
    const performer = performerName.trim();
    const email = accountEmail.trim();
    if (!heard || !performer || !email) {
      setError('Every field here is required.');
      return;
    }
    setError(null);
    setPhase('confirming');
  }

  async function confirm(): Promise<void> {
    setPhase('submitting');
    setError(null);
    try {
      const result = await confirmBetaInvite(token, {
        heardFrom: heardFrom.trim(),
        performerName: performerName.trim(),
        accountEmail: accountEmail.trim(),
      });
      if (result.accountMinted) {
        setPhase('minted');
        // The session cookie is already set — a full reload hands the app
        // its normal boot path (whoami() finds the new session, zero
        // tenants, lands in Onboarding), the same landing every other
        // account-minting link in this app uses rather than this screen
        // trying to reconstruct App.tsx's own signed-in state.
        window.location.assign('/');
        return;
      }
      setReceipt(
        result.receipt ?? `A link to create the account went to ${accountEmail.trim()} — so we know that inbox is theirs.`
      );
      setPhase('mailed');
    } catch (err) {
      setError(err instanceof ApiError && err.serverMessage ? err.serverMessage : 'That didn’t go through.');
      setPhase('confirming');
    }
  }

  if (phase === 'loading') {
    return (
      <div class="betainv">
        <div class="betainv__center">
          <Knot size={46} />
        </div>
      </div>
    );
  }

  if (phase === 'refused') {
    return (
      <div class="betainv">
        <div class="betainv__center">
          <Knot size={46} />
          <div class="betainv__say">{refusalMessage}</div>
        </div>
      </div>
    );
  }

  if (phase === 'minted') {
    return (
      <div class="betainv">
        <div class="betainv__center">
          <Knot size={46} />
          <div class="betainv__say">You’re in — one moment…</div>
        </div>
      </div>
    );
  }

  if (phase === 'mailed') {
    return (
      <div class="betainv">
        <div class="betainv__center">
          <Knot size={46} />
          <div class="betainv__eyebrow">Almost there</div>
          <div class="betainv__say">{receipt}</div>
        </div>
      </div>
    );
  }

  if (phase === 'confirming' || phase === 'submitting') {
    return (
      <div class="betainv">
        <Knot size={40} />
        {error ? (
          <div class="betainv__error" role="alert">
            {error}
          </div>
        ) : null}
        <div class="betainv__say">
          The account will be registered to <b>{accountEmail.trim()}</b>.
        </div>
        <button
          type="button"
          class="betainv__btn"
          disabled={phase === 'submitting'}
          onClick={() => void confirm()}
        >
          {phase === 'submitting' ? 'One moment…' : 'That’s right'}
        </button>
        <button
          type="button"
          class="betainv__textlink"
          disabled={phase === 'submitting'}
          onClick={() => setPhase('form')}
        >
          &lsaquo; Change it
        </button>
      </div>
    );
  }

  // phase === 'form'
  return (
    <div class="betainv">
      <Knot size={40} />
      <div class="betainv__eyebrow">Your beta invite</div>
      {error ? (
        <div class="betainv__error" role="alert">
          {error}
        </div>
      ) : null}
      <label class="betainv__label" for="betainv-heard-from">
        Where did you hear about CallToWrap?
      </label>
      <input
        id="betainv-heard-from"
        class="betainv__input"
        type="text"
        maxLength={HEARD_FROM_MAX}
        value={heardFrom}
        onInput={(e) => setHeardFrom((e.target as HTMLInputElement).value)}
      />
      <label class="betainv__label" for="betainv-performer-name">
        Performer name
      </label>
      <input
        id="betainv-performer-name"
        class="betainv__input"
        type="text"
        autocomplete="name"
        value={performerName}
        onInput={(e) => setPerformerName((e.target as HTMLInputElement).value)}
      />
      <label class="betainv__label" for="betainv-account-email">
        Account email
      </label>
      <input
        id="betainv-account-email"
        class="betainv__input"
        type="email"
        inputMode="email"
        autocomplete="email"
        value={accountEmail}
        onInput={(e) => setAccountEmail((e.target as HTMLInputElement).value)}
      />
      <button type="button" class="betainv__btn" onClick={goToConfirm}>
        Continue
      </button>
    </div>
  );
}
